import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Card, CardContent } from "@/components/ui/card";
import { HistoryDetailDialog } from "@/components/HistoryDetailDialog";
import { idSuffix } from "@/lib/idLabel";
import { formatDateTime } from "@/lib/dates";
import { toast } from "sonner";
import { RefreshCw, Truck, Factory, Package, Globe, Download } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";

const CHANNEL_LABELS = {
  everlantern: "Everlantern",
  hong_zheng: "Hong Zheng",
  newgates: "Newgates",
  hl: "H&L",
};
const PALETTE = ["#056839", "#A0522D", "#69A488", "#E4D5B7", "#3F3F3F", "#D2E4D6"];

export default function ViewerDashboard() {
  const [collections, setCollections] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [stock, setStock] = useState(null);
  const [busy, setBusy] = useState(false);

  async function loadAll() {
    setBusy(true);
    try {
      const [c, s, st] = await Promise.all([
        api.get("/admin/collections"),
        api.get("/admin/shipments"),
        api.get("/facility/stock-summary"),
      ]);
      setCollections(c.data || []);
      setShipments(s.data || []);
      setStock(st.data || null);
    } catch (_) {
      // silent on viewer; surface visually
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { loadAll(); }, []);

  async function downloadFullReport() {
    // Fetch the server-formatted CSV for all three record kinds so the
    // download carries the per-material columns (mat_/input_/output_/shipped_)
    // and consistent GMT+X timestamps. We concatenate them with section
    // headers so a single file still tells the full-report story.
    try {
      const [cRes, pRes, sRes] = await Promise.all([
        api.get("/admin/export.csv?kind=collections", { responseType: "text" }),
        api.get("/admin/export.csv?kind=processings", { responseType: "text" }),
        api.get("/admin/export.csv?kind=shipments", { responseType: "text" }),
      ]);
      const sections = [
        `# COLLECTIONS`, cRes.data || "",
        `\n# PROCESSINGS`, pRes.data || "",
        `\n# SHIPMENTS`, sRes.data || "",
      ];
      const csv = sections.join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `process-full-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded full report");
    } catch (e) {
      toast.error("Download failed");
    }
  }

  const totals = useMemo(() => ({
    collections_count: collections.length,
    collections_kg: collections.reduce((a, c) => a + (c.received_weight_kg || c.estimated_weight_kg || 0), 0),
    shipments_count: shipments.length,
    shipments_kg: shipments.reduce((a, s) => a + (s.total_shipped_kg || 0), 0),
  }), [collections, shipments]);

  const byChannel = useMemo(() => {
    const m = {};
    Object.keys(CHANNEL_LABELS).forEach(k => {
      m[k] = { id: k, name: CHANNEL_LABELS[k], coll: 0, kg_collected: 0, ship: 0, kg_shipped: 0 };
    });
    collections.forEach(c => {
      const k = c.channel_id;
      if (m[k]) {
        m[k].coll += 1;
        m[k].kg_collected += (c.received_weight_kg || c.estimated_weight_kg || 0);
      }
    });
    shipments.forEach(s => {
      const k = s.channel_id;
      if (m[k]) {
        m[k].ship += 1;
        m[k].kg_shipped += (s.total_shipped_kg || 0);
      }
    });
    return Object.values(m).map(c => ({
      ...c,
      kg_collected: Number(c.kg_collected.toFixed(1)),
      kg_shipped: Number(c.kg_shipped.toFixed(1)),
    }));
  }, [collections, shipments]);

  const materialBreakdown = useMemo(() => {
    const m = {};
    collections.forEach(c => {
      const mat = c.material_type || "Unknown";
      m[mat] = (m[mat] || 0) + (c.received_weight_kg || c.estimated_weight_kg || 0);
    });
    return Object.entries(m)
      .filter(([, kg]) => kg > 0)
      .map(([material, kg]) => ({ material, kg: Number(kg.toFixed(1)) }));
  }, [collections]);

  const weekly = useMemo(() => {
    const m = {};
    collections.forEach(c => {
      if (!c.timestamp) return;
      const d = new Date(c.timestamp);
      if (isNaN(d)) return;
      const onejan = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
      const key = `${d.getFullYear()}-W${String(week).padStart(2, "0")}`;
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).sort().map(([week, count]) => ({ week, count }));
  }, [collections]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="viewer-dashboard">
      <Topbar subtitle="Viewer (read-only)" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-[#000000]">
              Cross-channel overview
            </h1>
            <p className="text-sm text-[#3F3F3F]/70 mt-1">
              Read-only monitoring across all channels.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadFullReport}
              disabled={busy}
              data-testid="viewer-full-report-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#056839] hover:bg-[#044E2A] text-white rounded-lg disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              Download full report
            </button>
            <button
              onClick={loadAll}
              disabled={busy}
              data-testid="viewer-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#D2E4D6] rounded-lg bg-white hover:bg-[#F9F8F6] disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI
            testId="viewer-kpi-collections"
            label="Collections"
            value={totals.collections_count}
            sub={`${totals.collections_kg.toFixed(0)} kg total`}
            icon={<Truck className="w-5 h-5" />}
          />
          <KPI
            testId="viewer-kpi-shipments"
            label="Shipments"
            value={totals.shipments_count}
            sub={`${totals.shipments_kg.toFixed(0)} kg total`}
            icon={<Factory className="w-5 h-5" />}
          />
          <KPI
            testId="viewer-kpi-stock"
            label="Processed in stock"
            value={stock ? `${stock.total_in_stock_kg.toFixed(0)} kg` : "—"}
            sub="Across all channels"
            icon={<Package className="w-5 h-5" />}
          />
          <KPI
            testId="viewer-kpi-channels"
            label="Active channels"
            value={byChannel.filter(c => c.coll + c.ship > 0).length}
            sub={`of ${byChannel.length}`}
            icon={<Globe className="w-5 h-5" />}
          />
        </div>

        {/* Per-channel breakdown */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="viewer-channel-table">
              <thead className="bg-[#D2E4D6]/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3 text-right">Collections</th>
                  <th className="px-4 py-3 text-right">Kg collected</th>
                  <th className="px-4 py-3 text-right">Shipments</th>
                  <th className="px-4 py-3 text-right">Kg shipped</th>
                </tr>
              </thead>
              <tbody>
                {byChannel.map(c => (
                  <tr
                    key={c.id}
                    className="border-t border-[#D2E4D6]/60"
                    data-testid={`viewer-channel-row-${c.id}`}
                  >
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.coll}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.kg_collected.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.ship}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.kg_shipped.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Kg collected per channel" testId="viewer-chart-coll">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byChannel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="kg_collected" fill="#056839" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Kg shipped per channel" testId="viewer-chart-ship">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byChannel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="kg_shipped" fill="#A0522D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Material mix (collections)" testId="viewer-chart-materials">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Pie
                  data={materialBreakdown}
                  dataKey="kg"
                  nameKey="material"
                  outerRadius={80}
                  label={(d) => d.material}
                >
                  {materialBreakdown.map((entry, i) => (
                    <Cell key={entry.material || `mat-${i}`} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Collections over time (weekly)" testId="viewer-chart-weekly">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#056839" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Historical records (read-only) */}
        <ViewerHistoryPanel
          collections={collections}
          shipments={shipments}
        />
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, testId }) {
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#69A488] font-medium">
              {label}
            </div>
            <div className="font-heading text-3xl font-semibold tracking-tight mt-2 text-[#000000]">
              {value}
            </div>
            <div className="text-xs text-[#3F3F3F]/70 mt-1">{sub}</div>
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D2E4D6] text-[#056839]">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={testId}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-[#69A488] font-semibold mb-3">
          {title}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}


// ============================================================================
// ViewerHistoryPanel — read-only historical records with filters + CSV export
// ============================================================================
function ViewerHistoryPanel({ collections, shipments }) {
  const [tab, setTab] = useState("collections");
  const [processings, setProcessings] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    material: "",
    channel: "",
  });

  useEffect(() => {
    setLoadingProc(true);
    api.get("/admin/processings")
      .then((r) => setProcessings(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingProc(false));
  }, []);

  const materials = useMemo(() => {
    const s = new Set();
    collections.forEach((c) => c.material_type && s.add(c.material_type));
    shipments.forEach((sh) => (sh.destinations || []).forEach((d) => d.material_type && s.add(d.material_type)));
    return Array.from(s).sort();
  }, [collections, shipments]);

  // Stable filter builder — depends only on `filters` so each useMemo
  // below only recomputes when the user actually changes a filter.
  const applyCommonFilters = useCallback((getDate, getMaterial, getChannel) => {
    return (item) => {
      const dt = getDate(item);
      if (filters.from && dt && dt < new Date(filters.from)) return false;
      if (filters.to) {
        const toDate = new Date(filters.to);
        toDate.setHours(23, 59, 59, 999);
        if (dt && dt > toDate) return false;
      }
      if (filters.channel && getChannel(item) !== filters.channel) return false;
      if (filters.material) {
        const m = getMaterial(item);
        if (Array.isArray(m)) {
          if (!m.includes(filters.material)) return false;
        } else if (m !== filters.material) return false;
      }
      return true;
    };
  }, [filters]);

  const visibleCollections = useMemo(() => {
    return [...collections]
      .filter(applyCommonFilters(
        (c) => c.timestamp ? new Date(c.timestamp) : null,
        (c) => c.material_type,
        (c) => c.channel_id,
      ))
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [collections, applyCommonFilters]);

  const visibleProcessings = useMemo(() => {
    return [...processings]
      .filter(applyCommonFilters(
        (p) => p.processing_date ? new Date(p.processing_date) : null,
        (p) => p.processing_types || [],
        (p) => p.channel_id,
      ))
      .sort((a, b) => new Date(b.processing_date || 0) - new Date(a.processing_date || 0));
  }, [processings, applyCommonFilters]);

  const visibleShipments = useMemo(() => {
    return shipments
      .filter(applyCommonFilters(
        (s) => s.created_at ? new Date(s.created_at) : null,
        (s) => (s.destinations || []).map((d) => d.material_type),
        (s) => s.channel_id,
      ))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [shipments, applyCommonFilters]);

  async function downloadCsv() {
    // Route to server-formatted CSV so the tab-specific download carries
    // per-material columns and consistent GMT+X timestamps.
    const kind = tab === "processed" ? "processings" : tab === "shipped" ? "shipments" : "collections";
    const params = new URLSearchParams({ kind });
    if (filters.from) params.set("date_from", filters.from);
    if (filters.to) params.set("date_to", filters.to);
    if (filters.material) params.set("material_type", filters.material);
    if (filters.channel) params.set("channel_id", filters.channel);
    try {
      const res = await api.get(`/admin/export.csv?${params}`, { responseType: "blob" });
      if (!res.data || res.data.size < 30) { toast("No records to download"); return; }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `viewer-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded CSV");
    } catch (e) {
      toast.error("Download failed");
    }
  }

  function resetFilters() {
    setFilters({ from: "", to: "", material: "", channel: "" });
  }

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="viewer-history-panel">
      <CardContent className="p-5 md:p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Historical records</div>
          <h3 className="font-heading text-lg font-semibold text-[#000000]">All activity (read-only)</h3>
        </div>

        {/* Sub-tabs */}
        <div className="flex items-center gap-2" data-testid="viewer-history-subtabs">
          {[
            { v: "collections", l: `Collections (${visibleCollections.length})`, accent: "bg-[#D2E4D6] text-[#056839]" },
            { v: "processed",   l: `Processed (${visibleProcessings.length})`,  accent: "bg-[#E4D5B7] text-[#A0522D]" },
            { v: "shipped",     l: `Shipped (${visibleShipments.length})`,      accent: "bg-[#056839] text-white" },
          ].map(s => (
            <button key={s.v} type="button" onClick={() => setTab(s.v)}
              data-testid={`viewer-history-subtab-${s.v}`}
              className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
                tab === s.v ? s.accent + " shadow-sm" : "text-[#3F3F3F]/60 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
              }`}>{s.l}</button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3" data-testid="viewer-history-filters">
          <div className="min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">From</label>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              data-testid="viewer-filter-from"
              className="h-9 mt-1 w-full text-sm rounded-md border border-[#D2E4D6] bg-white px-3" />
          </div>
          <div className="min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">To</label>
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              data-testid="viewer-filter-to"
              className="h-9 mt-1 w-full text-sm rounded-md border border-[#D2E4D6] bg-white px-3" />
          </div>
          <div className="min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Material</label>
            <select value={filters.material} onChange={(e) => setFilters({ ...filters, material: e.target.value })}
              data-testid="viewer-filter-material"
              className="h-9 mt-1 w-full text-sm rounded-md border border-[#D2E4D6] bg-white px-3">
              <option value="">All materials</option>
              {materials.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Channel</label>
            <select value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
              data-testid="viewer-filter-channel"
              className="h-9 mt-1 w-full text-sm rounded-md border border-[#D2E4D6] bg-white px-3">
              <option value="">All channels</option>
              {Object.entries(CHANNEL_LABELS).map(([k, name]) => <option key={k} value={k}>{name}</option>)}
            </select>
          </div>
          <button type="button" onClick={resetFilters} data-testid="viewer-history-reset"
            className="text-xs text-[#3F3F3F]/70 underline hover:text-[#056839] h-9 px-2">Reset</button>
          <button onClick={downloadCsv} data-testid="viewer-history-export"
            className="h-9 px-4 inline-flex items-center gap-1.5 text-sm bg-[#056839] hover:bg-[#044E2A] text-white rounded-lg">
            <Download className="w-4 h-4" /> Download CSV
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-[#D2E4D6]/60">
          {tab === "collections" ? (
            <table className="w-full text-sm" data-testid="viewer-history-table-collections">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-right">Kg</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Facility</th><th className="px-3 py-2 text-left">Collector</th></tr>
              </thead>
              <tbody>
                {visibleCollections.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#3F3F3F]/60">No collections match.</td></tr>}
                {visibleCollections.map((c) => (
                  <tr key={c.id} onClick={() => setDetail({ kind: "received", data: c, type: "collection", id: c.id })}
                    data-testid={`viewer-row-coll-${c.id}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(c.timestamp)}</td>
                    <td className="px-3 py-2">{CHANNEL_LABELS[c.channel_id] || "—"}</td>
                    <td className="px-3 py-2">{c.material_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(c.received_weight_kg || c.estimated_weight_kg || 0).toFixed(1)}</td>
                    <td className="px-3 py-2">{c.status}</td>
                    <td className="px-3 py-2">{c.facility_name || "—"}</td>
                    <td className="px-3 py-2">{c.collector_name ? `${c.collector_name}${idSuffix(c.collector_id)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "processed" ? (
            <table className="w-full text-sm" data-testid="viewer-history-table-processed">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Facility</th><th className="px-3 py-2 text-left">Types</th><th className="px-3 py-2 text-right">Input kg</th></tr>
              </thead>
              <tbody>
                {loadingProc && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#3F3F3F]/60">Loading…</td></tr>}
                {!loadingProc && visibleProcessings.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#3F3F3F]/60">No processing records match.</td></tr>}
                {visibleProcessings.map((p) => (
                  <tr key={p.id} onClick={() => setDetail({ kind: "processed", data: p, type: "processing", id: p.id })}
                    data-testid={`viewer-row-proc-${p.id}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(p.processing_date)}</td>
                    <td className="px-3 py-2">{CHANNEL_LABELS[p.channel_id] || "—"}</td>
                    <td className="px-3 py-2">{p.facility_name || "—"}</td>
                    <td className="px-3 py-2">{(p.processing_types || []).join(", ")}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(p.total_input_weight_kg || 0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="viewer-history-table-shipped">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Facility</th><th className="px-3 py-2 text-left">Destination</th><th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-right">Kg</th><th className="px-3 py-2 text-left">Status</th></tr>
              </thead>
              <tbody>
                {visibleShipments.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#3F3F3F]/60">No shipments match.</td></tr>}
                {visibleShipments.flatMap((s) => (s.destinations || [{}]).map((d, i) => (
                  <tr key={`${s.id}-${i}`} onClick={() => setDetail({ kind: "shipped", data: { shipment: s, destination: d }, type: "shipment", id: s.id, admin_status: s.admin_status })}
                    data-testid={`viewer-row-ship-${s.id}-${i}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(s.created_at)}</td>
                    <td className="px-3 py-2">{CHANNEL_LABELS[s.channel_id] || "—"}</td>
                    <td className="px-3 py-2">{s.facility_name || "—"}</td>
                    <td className="px-3 py-2">{d.destination_name || s.destination_name || "—"}</td>
                    <td className="px-3 py-2">{d.material_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(d.weight_kg || s.total_shipped_kg || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 capitalize">{s.admin_status || "pending"}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
      <HistoryDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </Card>
  );
}
